import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useState,
} from "react"
import { Button, Card, Form } from "react-bootstrap"

import { LineSeriesOption } from "echarts"
import {
  PrevetSpeedGraphZoneType,
  getZonesOfConcernWithSinglePointZoneIndex,
} from "../getSpeedChartOptionsHelpers"
import { useFetchVesselLegs } from "../hooks/useFetchVesselLegs"
import { PrevetSpeedGraphModal } from "./PrevetSpeedGraphModal"
import { MemoPrevetVesselSpeedChart } from "./PrevetVesselSpeedChart"
import { VesselLeg } from "../hooks/usePrevetAPI"
import { useWoopraAPI } from "instrumentation/tracking/useWoopraApi"
import { VesselSegmentInputSelection } from "./PrevetMain"
import { average as findAverage } from "simple-statistics"

interface PrevetVesselSpeedProps {
  vesselImo: number
  vesselName: string
  setIsTradingModalShown: React.Dispatch<React.SetStateAction<boolean>>
  setDateForCenterMarker: React.Dispatch<
    React.SetStateAction<undefined | string>
  >
  setChartLadenStatus: React.Dispatch<React.SetStateAction<boolean | undefined>>
  setLadenAvgSpeed: Dispatch<React.SetStateAction<undefined | number>>
  setBallastAvgSpeed: Dispatch<React.SetStateAction<undefined | number>>
  setLadenMaxSpeed: Dispatch<React.SetStateAction<undefined | number>>
  setBallastMaxSpeed: Dispatch<React.SetStateAction<undefined | number>>
  ladenChartInstance: MutableRefObject<any>
  ballastChartInstance: MutableRefObject<any>
  userLadenVesselSpeed: string | undefined
  userBallastVesselSpeed: string | undefined
  endDate: string
  setPrevetSpeedGraphZoneType: Dispatch<
    SetStateAction<PrevetSpeedGraphZoneType | undefined>
  >
  prevetSpeedGraphZoneType: PrevetSpeedGraphZoneType | undefined
  setVesselSpeedDateRange: Dispatch<SetStateAction<string>>
  vesselSpeedDateRange: string
  describedBallastSpeed: string
  describedLadenSpeed: string
  chosenSegment: VesselSegmentInputSelection
  isShowSeaMarginChecked: boolean
  setIsShowSeaMarginChecked: (value: boolean) => void
  setBallastAvgMargin: (value: number) => void
  setLadenAvgMargin: (value: number) => void
}

export function PrevetVesselSpeedWrapper({
  vesselImo,
  vesselName,
  setIsTradingModalShown,
  setDateForCenterMarker,
  setChartLadenStatus,
  setLadenAvgSpeed,
  setBallastAvgSpeed,
  setLadenMaxSpeed,
  setBallastMaxSpeed,
  ladenChartInstance,
  ballastChartInstance,
  userLadenVesselSpeed,
  userBallastVesselSpeed,
  endDate,
  setPrevetSpeedGraphZoneType,
  prevetSpeedGraphZoneType,
  setVesselSpeedDateRange,
  vesselSpeedDateRange,
  describedBallastSpeed,
  describedLadenSpeed,
  chosenSegment,
  isShowSeaMarginChecked,
  setIsShowSeaMarginChecked,
  setBallastAvgMargin,
  setLadenAvgMargin,
}: PrevetVesselSpeedProps) {
  const { postTrackingEvent } = useWoopraAPI()
  /* radio button for 6 months and 1 year */
  // const [selectedOption, setSelectedOption] = useState<string>(DEFAULT_SPEED_DATE_RANGE)
  const { vesselLegsData } = useFetchVesselLegs({
    imo: vesselImo,
    end: endDate,
    start: vesselSpeedDateRange,
  })
  const [isSpeedGraphModalShown, setIsSpeedGraphModalShown] = useState(false)
  const [isStraitsCanalsChecked, setIsStraitsCanalsChecked] = useState(false)
  /* const [isShowSeaMarginChecked, setIsShowSeaMarginChecked] = useState(true) */

  const [ladenFilteredLegs, setLadenFilteredLegs] = useState<VesselLeg[]>([])
  const [ballastFilteredLegs, setBallastFilteredLegs] = useState<VesselLeg[]>(
    []
  )

  function handleChangeZoneType(id: string | undefined) {
    setPrevetSpeedGraphZoneType(id as PrevetSpeedGraphZoneType)
    if (!vesselLegsData || vesselLegsData.length < 1) {
      return
    }

    const ladenSource = ladenFilteredLegs.length
      ? ladenFilteredLegs
      : vesselLegsData.filter((d) => d.laden)
    const ballastSource = ballastFilteredLegs.length
      ? ballastFilteredLegs
      : vesselLegsData.filter((d) => !d.laden)

    const ladenOpts = ladenChartInstance.current.getOption()
    const ladenSeries = [...(ladenOpts.series as any[])]

    const {
      zonesOfConcern: ladenZones,
      indexOfSinglePointZones: ladenIndexOfSinglePointZones,
    } = getZonesOfConcernWithSinglePointZoneIndex({
      speedData: ladenSource,
      isLaden: true,
      useForMinTreshold: id as PrevetSpeedGraphZoneType,
    })

    // update zones on the main speed series (index 0)
    ladenSeries[0] = {
      ...ladenSeries[0],
      markArea: {
        ...(ladenSeries[0].markArea || {}),
        data: ladenZones,
      },
    }

    // update the “single point zone hack” series (index 2) ONLY if it exists
    if (ladenSeries[2]) {
      ladenSeries[2] = {
        ...ladenSeries[2],
        // IMPORTANT: keep the SAME indices as original (xAxisIndex should stay 2)
        markArea: {
          ...(ladenSeries[2].markArea || {}),
          data: ladenIndexOfSinglePointZones.map((idx) => [
            { xAxis: idx * 2 },
            { xAxis: idx * 2 + 1 },
          ]),
        },
      }
    }

    ladenChartInstance.current.setOption(
      { ...ladenOpts, series: ladenSeries },
      false
    )

    /*  ladenChartInstance.current.setOption({
      ...ladenOpts,
      series: [
        // Order of ladenOpts items in series array has to match initial order
        {
          ...(ladenOpts.series as LineSeriesOption[])[0],
          markArea: {
            ...(ladenOpts.series as LineSeriesOption[])[0].markArea,
            data: ladenZones,
          },
        },
        {
          ...(ladenOpts.series as LineSeriesOption[])[1],
        },
        {
          type: "line",
          xAxisIndex: 1,
          markArea: {
            // Enables the hack to draw a mark area around a single point
            ...(ladenOpts.series as LineSeriesOption[])[2].markArea,
            data: ladenIndexOfSinglePointZones.map((idx) => {
              return [{ xAxis: idx * 2 }, { xAxis: idx * 2 + 1 }]
            }),
          },
        },
      ],
    }) */

    const ballastOpts = ballastChartInstance.current.getOption()
    const ballastSeries = [...(ballastOpts.series as any[])]

    const {
      zonesOfConcern: ballastZones,
      indexOfSinglePointZones: ballastIndexOfSinglePointZones,
    } = getZonesOfConcernWithSinglePointZoneIndex({
      speedData: ballastSource,
      isLaden: false,
      useForMinTreshold: id as PrevetSpeedGraphZoneType,
    })

    // update zones on the main speed series (index 0)
    ballastSeries[0] = {
      ...ballastSeries[0],
      markArea: {
        ...(ballastSeries[0].markArea || {}),
        data: ballastZones,
      },
    }

    // update the “single point zone hack” series (index 2) ONLY if it exists
    if (ballastSeries[2]) {
      ballastSeries[2] = {
        ...ballastSeries[2],
        // IMPORTANT: keep the SAME indices as original (xAxisIndex should stay 2)
        markArea: {
          ...(ballastSeries[2].markArea || {}),
          data: ballastIndexOfSinglePointZones.map((idx) => [
            { xAxis: idx * 2 },
            { xAxis: idx * 2 + 1 },
          ]),
        },
      }
    }

    ballastChartInstance.current.setOption(
      { ...ballastOpts, series: ballastSeries },
      false
    )

    /* ballastChartInstance.current.setOption({
      ...ballastOpts,
      series: [
        // Order of ladenOpts items in series array has to match initial order
        {
          ...(ballastOpts.series as LineSeriesOption[])[0],
          markArea: {
            ...(ballastOpts.series as LineSeriesOption[])[0].markArea,
            data: ballastZones,
          },
        },
        {
          ...(ballastOpts.series as LineSeriesOption[])[1],
        },
        {
          type: "line",
          xAxisIndex: 1,
          markArea: {
            // Enables the hack to draw a mark area around a single point
            ...(ballastOpts.series as LineSeriesOption[])[2].markArea,
            data: ballastIndexOfSinglePointZones.map((idx) => {
              return [{ xAxis: idx * 2 }, { xAxis: idx * 2 + 1 }]
            }),
          },
        },
      ],
    }) */
  }

  useEffect(() => {
    handleChangeZoneType(prevetSpeedGraphZoneType)
  }, [prevetSpeedGraphZoneType])

  const handleOptionChange = (value: string) => {
    setVesselSpeedDateRange(value)
  }

  //calculating global minmax

  // Min is computed per chart so laden and ballast y-axes are independent
  const [ladenMin, setLadenMin] = useState<number>(5) // Default min
  const [ballastMin, setBallastMin] = useState<number>(5) // Default min
  const [globalMax, setGlobalMax] = useState<number>(14) // Default max

  useEffect(() => {
    if (vesselLegsData) {
      const ladenSpeeds = vesselLegsData
        .filter((data) => data.laden)
        .map((item) => item.sog)
      const ballastSpeeds = vesselLegsData
        .filter((data) => !data.laden)
        .map((item) => item.sog)

      const allSpeeds = [...ladenSpeeds, ...ballastSpeeds]
      const maxSpeed = Math.max(...allSpeeds)

      // Use data value if lower than 5, else default to 5
      const getAxisMin = (speeds: number[]) =>
        speeds.length ? Math.floor(Math.min(Math.min(...speeds), 5)) : 5
      setLadenMin(getAxisMin(ladenSpeeds))
      setBallastMin(getAxisMin(ballastSpeeds))
      setGlobalMax(Math.max(maxSpeed, 14)) // Use data value if higher than 14, else default to 14

      //set margin for both ballast and laden
      ballastSpeeds.length > 0 &&
        setBallastAvgMargin(
          findAverage(
            vesselLegsData
              .filter((data) => !data.laden)
              .map((item) => (item.margin ? item.margin : 0))
          )
        )
      ladenSpeeds.length > 0 &&
        setLadenAvgMargin(
          findAverage(
            vesselLegsData
              .filter((data) => data.laden)
              .map((item) => (item.margin ? item.margin : 0))
          )
        )
    }
  }, [vesselLegsData])


  return (
    <Card.Body className="PrevetTrio_SpeedGraph_Inner">
      {isSpeedGraphModalShown && (
        <PrevetSpeedGraphModal
          isSpeedGraphModalShown={isSpeedGraphModalShown}
          vesselImo={vesselImo}
          vesselName={vesselName}
          closeSpeedGraphModal={() => {
            setIsSpeedGraphModalShown(false)
          }}
          userLadenVesselSpeed={userLadenVesselSpeed}
          userBallastVesselSpeed={userBallastVesselSpeed}
          setIsTradingModalShown={setIsTradingModalShown}
          setDateForCenterMarker={setDateForCenterMarker}
          setChartLadenStatus={setChartLadenStatus}
          endDate={endDate}
          startDate={vesselSpeedDateRange}
          ladenMin={ladenMin}
          ballastMin={ballastMin}
          globalMax={globalMax}
          ladenLegsData={ladenFilteredLegs}
          ballastLegsData={ballastFilteredLegs}
          describedBallastSpeed={Number(describedBallastSpeed)}
          describedLadenSpeed={Number(describedLadenSpeed)}
          isShowSeaMarginChecked={isShowSeaMarginChecked}
        />
      )}
      <div className="mb-0">
        <div className="mb-0 ">
          <div
            className="d-flex mb-4"
            style={{
              width: "calc(100% - 10px)",
              display: "flex",
              marginBottom: "1rem",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h4
              className="card-title mb-0 me-2"
              style={{
                alignContent: "center",
                width: "90px",
                whiteSpace: "nowrap",
              }}
            >
              Vessel Speed
            </h4>
            <div
              className="d-flex align-items-center justify-content-space-between"
              style={{ width: "90vw", flexWrap: "wrap", paddingLeft: "5px" }}
            >
              <Form className="d-flex flex-fill align-items-start justify-content-start">
                <div
                  style={{
                    display: "flex",
                    width: "85px",
                    alignItems: "center",
                  }}
                >
                  <Form.Check
                    type="radio"
                    id="sixMonths"
                    name="duration"
                    value="6 Months"
                    checked={vesselSpeedDateRange === "6 Months"}
                    onChange={(event) => {
                      handleOptionChange(event.target.value)
                      postTrackingEvent({
                        page: "/prevetting",
                        event: "click_speed_history",
                        properties: {
                          checkbox: "six_months",
                          vesselSegment: chosenSegment.value,
                          component: "speed_chart",
                        },
                      })
                    }}
                  />
                  <Form.Label
                    style={{ whiteSpace: "nowrap", marginBottom: "0" }}
                  >
                    6 Months
                  </Form.Label>
                </div>
                <div
                  style={{
                    display: "flex",
                    width: "85px",
                    alignItems: "center",
                  }}
                >
                  <Form.Check
                    type="radio"
                    id="oneYear"
                    name="duration"
                    value="1 Year"
                    checked={vesselSpeedDateRange === "1 Year"}
                    onChange={(event) => {
                      handleOptionChange(event.target.value)
                      postTrackingEvent({
                        page: "/prevetting",
                        event: "click_speed_history",
                        properties: {
                          checkbox: "one_year",
                          vesselSegment: chosenSegment.value,
                          component: "speed_chart",
                        },
                      })
                    }}
                  />
                  <Form.Label
                    style={{ whiteSpace: "nowrap", marginBottom: "0" }}
                  >
                    1 Year
                  </Form.Label>
                </div>
              </Form>
              <Form className="d-flex no-wrap me-3">
                {/* Straits / Canals checkbox — can be toggled freely */}
                {/* <Form.Check
                  className="d-flex align-items-start me-2"
                  type="checkbox"
                >
                  <Form.Check.Input
                    type="checkbox"
                    id="Straits / Canals"
                    style={{ float: "revert" }}
                    onChange={() => {
                      setIsStraitsCanalsChecked(!isStraitsCanalsChecked)
                      postTrackingEvent({
                        page: "/prevetting",
                        event: "click_canals",
                        properties: {
                          checkbox: "straits_canals",
                          vesselSegment: chosenSegment.value,
                          component: "speed_chart",
                        },
                      })
                    }}
                    checked={isStraitsCanalsChecked}
                  />
                  <Form.Check.Label
                    style={{ width: "unset" }}
                    className="m-0 ms-1 mt-0"
                  >
                    Straits / Canals
                  </Form.Check.Label>
                </Form.Check> */}
                <Form.Check
                  className="d-flex align-items-start me-2"
                  type="checkbox"
                >
                  <Form.Check.Input
                    type="checkbox"
                    id={PrevetSpeedGraphZoneType.Outliers}
                    style={{ float: "revert" }}
                    checked={
                      prevetSpeedGraphZoneType ===
                      PrevetSpeedGraphZoneType.Outliers
                    }
                    onChange={() => {
                      const newValue =
                        prevetSpeedGraphZoneType ===
                        PrevetSpeedGraphZoneType.Outliers
                          ? PrevetSpeedGraphZoneType.None
                          : PrevetSpeedGraphZoneType.Outliers
                      setPrevetSpeedGraphZoneType(newValue)
                      postTrackingEvent({
                        page: "/prevetting",
                        event: "click_zone_type",
                        properties: {
                          checkbox: PrevetSpeedGraphZoneType.Outliers,
                          checked: String(
                            newValue === PrevetSpeedGraphZoneType.Outliers
                          ),
                          vesselSegment: chosenSegment.value,
                          component: "speed_chart",
                        },
                      })
                    }}
                  />
                  <Form.Check.Label
                    style={{ width: "unset" }}
                    className="m-0 ms-1 mt-0"
                  >
                    Outliers
                  </Form.Check.Label>
                </Form.Check>

                <Form.Check className="d-flex flex-nowrap align-items-start">
                  <Form.Check.Input
                    type="checkbox"
                    id="seamargin"
                    style={{ float: "revert" }}
                    onChange={() => {
                      setIsShowSeaMarginChecked(!isShowSeaMarginChecked)
                    }}
                    checked={isShowSeaMarginChecked}
                  />
                  <Form.Check.Label
                    style={{ width: "unset" }}
                    className="m-0 ms-1 mt-0 nowrap"
                  >
                    Sea Margin
                  </Form.Check.Label>
                </Form.Check>
              </Form>
            </div>
            <Button
              className="PrevetTrio_SpeedGraph_OceanZoneButton_VesselSpeed"
              onClick={() => {
                setIsSpeedGraphModalShown(true)
                postTrackingEvent({
                  page: "/prevetting",
                  event: "click_ocean_zones",
                  properties: {
                    button: "ocean_zones",
                    vesselSegment: chosenSegment.value,
                    component: "speed_chart",
                  },
                })
              }}
            >
              Ocean Zones
            </Button>
          </div>
          <div className="d-flex flex-column gap-4 w-100">
            <MemoPrevetVesselSpeedChart
              vesselImo={vesselImo}
              vesselName={vesselName}
              isLaden={true}
              setAvgSpeed={setLadenAvgSpeed}
              setMaxSpeed={setLadenMaxSpeed}
              setIsTradingModalShown={setIsTradingModalShown}
              setDateForCenterMarker={setDateForCenterMarker}
              setChartLadenStatus={setChartLadenStatus}
              chartInstance={ladenChartInstance}
              useForMinTreshold={
                prevetSpeedGraphZoneType || PrevetSpeedGraphZoneType.Outliers
              }
              endDate={endDate}
              startDate={vesselSpeedDateRange}
              globalMin={ladenMin}
              globalMax={globalMax}
              isShowStraitsCanalsChecked={isStraitsCanalsChecked}
              onFilteredVesselLegsChange={setLadenFilteredLegs}
              descSpeed={Number(describedLadenSpeed)}
              chosenSegment={chosenSegment}
              isShowSeaMarginChecked={isShowSeaMarginChecked}
            />
            <MemoPrevetVesselSpeedChart
              vesselImo={vesselImo}
              vesselName={vesselName}
              isLaden={false}
              setAvgSpeed={setBallastAvgSpeed}
              setMaxSpeed={setBallastMaxSpeed}
              setIsTradingModalShown={setIsTradingModalShown}
              setDateForCenterMarker={setDateForCenterMarker}
              setChartLadenStatus={setChartLadenStatus}
              chartInstance={ballastChartInstance}
              useForMinTreshold={
                prevetSpeedGraphZoneType || PrevetSpeedGraphZoneType.Outliers
              }
              endDate={endDate}
              startDate={vesselSpeedDateRange}
              globalMin={ballastMin}
              globalMax={globalMax}
              isShowStraitsCanalsChecked={isStraitsCanalsChecked}
              onFilteredVesselLegsChange={setBallastFilteredLegs}
              descSpeed={Number(describedBallastSpeed)}
              chosenSegment={chosenSegment}
              isShowSeaMarginChecked={isShowSeaMarginChecked}
            />
          </div>
        </div>
      </div>
    </Card.Body>
  )
}
